import { LogicalOperatorEnum } from '../models/enum';
import {
    isString,
    toNumber,
    isArray,
    isEmpty,
    toString,
    isUndefined,
    isNull,
    find,
    findLast,
    isNil,
    join,
} from 'lodash-es';

export class Utils {
    static isEmpty = (val: any): val is undefined | null => val === undefined || val === null;
    static isNotEmpty = <T>(val: T | undefined | null): val is T => !isEmpty(val);

    static getListNameFromPropId(propId?: string | null): string {
        const token = '.';

        if (!propId) return '';

        let iToken = propId.lastIndexOf(token);

        if (iToken <= 0) return '';

        return propId.substring(0, iToken);
    }

    static getPropertyNameFromPropId(propId?: string | null): string {
        const token = '.';

        if (!propId) return '';

        let iToken = propId.lastIndexOf(token);

        return propId.substring(iToken + 1, propId.length);
    }

    static checkValue(
        valueToCheck: unknown,
        matchValue: unknown,
        logicalOperator: LogicalOperatorEnum,
        itemListPropertyName?: string,
    ): boolean | null | unknown {
        switch (logicalOperator) {
            case LogicalOperatorEnum.Equals:
                return Utils.checkValueEquals(valueToCheck, matchValue);

            case LogicalOperatorEnum.NotEquals:
                return !Utils.checkValueEquals(valueToCheck, matchValue);

            case LogicalOperatorEnum.GreaterThan:
            case LogicalOperatorEnum.GreaterOrEquals:
            case LogicalOperatorEnum.LessThan:
            case LogicalOperatorEnum.LessOrEquals:
                return Utils.checkValueNumberOperations(valueToCheck, matchValue, logicalOperator);

            case LogicalOperatorEnum.IsNotNull:
                return Utils.checkValueNotNull(valueToCheck);

            case LogicalOperatorEnum.IsNull:
                return !Utils.checkValueNotNull(valueToCheck);

            case LogicalOperatorEnum.StartsWith:
            case LogicalOperatorEnum.EndsWith:
            case LogicalOperatorEnum.Contains:
            case LogicalOperatorEnum.ContainedIn:
            case LogicalOperatorEnum.LenEquals:
            case LogicalOperatorEnum.LenNotEquals:
            case LogicalOperatorEnum.LenGreaterThan:
            case LogicalOperatorEnum.LenGreaterOrEquals:
            case LogicalOperatorEnum.LenLessThan:
            case LogicalOperatorEnum.LenLessOrEquals:
                return Utils.checkValueArrayOperations(valueToCheck, matchValue, logicalOperator);

            case LogicalOperatorEnum.GetFirstValueNotNull:
            case LogicalOperatorEnum.GetLastValueNotNull:
            case LogicalOperatorEnum.GetFirstValueEquals:
            case LogicalOperatorEnum.GetLastValueEquals:
            case LogicalOperatorEnum.GetFirstValueNotEquals:
            case LogicalOperatorEnum.GetLastValueNotEquals:
            case LogicalOperatorEnum.GetFirstValueContains:
            case LogicalOperatorEnum.GetLastValueContains:
            case LogicalOperatorEnum.GetFirstValueContainedIn:
            case LogicalOperatorEnum.GetLastValueContainedIn:
                return Utils.getValueArrayOperations(
                    valueToCheck,
                    matchValue,
                    logicalOperator,
                    itemListPropertyName ?? '',
                );

            default:
                return null;
        }
    }

    static checkValueEquals(valueToCheck: unknown, matchValue: unknown): boolean {
        const isValueToCheckNull: boolean = !Utils.checkValueNotNull(valueToCheck);
        const isMatchValueNull: boolean = !Utils.checkValueNotNull(matchValue);

        if (isValueToCheckNull) {
            //true se sono entrambi nulli, false altrimenti
            return isMatchValueNull;
        }

        if (isMatchValueNull) return false; //matchCase nullo e colonna non nulla

        //se arrivo qui sono entrambi non nulli

        if (matchValue == valueToCheck) {
            return true; //il match ha avuto successo
        } else {
            //converto tutto in stringa e ci riprovo
            const strValueToCheck = toString(valueToCheck).toLowerCase();
            const strMatchValue = toString(matchValue).toLowerCase();

            let dateToCompare: Date = new Date(strMatchValue);

            if (!isNaN(dateToCompare.getDate())) {
                //il match value è una data
                let dateToCheck: Date = new Date(strValueToCheck);

                return dateToCheck.toLocaleString() == dateToCompare.toLocaleString();
            } else {
                //provo anche a comparare le stringhe
                return strValueToCheck == strMatchValue;
            }
        }
    }

    static checkValueNotNull(valueToCheck: unknown): boolean {
        const ret = isUndefined(valueToCheck) || isNull(valueToCheck) || valueToCheck === '';

        return !ret;
    }

    static checkValueNumberOperations(
        valueToCheck: unknown,
        matchValue: unknown,
        logicalOperator: LogicalOperatorEnum,
    ): boolean {
        const isValueToCheckNull: boolean = !Utils.checkValueNotNull(valueToCheck);
        const isMatchValueNull: boolean = !Utils.checkValueNotNull(matchValue);

        if (isValueToCheckNull || isMatchValueNull) return false;

        //provo convertire i parametri prima in date e poi in Number
        let nValueToCheck: any = Number(valueToCheck);
        let valueToCheckIsNumber: boolean = !isNaN(nValueToCheck);

        if (!valueToCheckIsNumber) {
            const strValueToCheck = toString(valueToCheck).toLowerCase();
            nValueToCheck = new Date(strValueToCheck);
        }

        let nMatchValue: any = Number(matchValue);
        let matchValueIsNumber: boolean = !isNaN(nMatchValue);

        if (!matchValueIsNumber) {
            const strMatchValue = toString(matchValue).toLowerCase();
            nMatchValue = new Date(strMatchValue);
        }

        if (isNaN(nValueToCheck) || isNaN(nMatchValue)) return false;

        switch (logicalOperator) {
            case LogicalOperatorEnum.GreaterThan:
                return nValueToCheck > nMatchValue;

            case LogicalOperatorEnum.GreaterOrEquals:
                return nValueToCheck >= nMatchValue;

            case LogicalOperatorEnum.LessThan:
                return nValueToCheck < nMatchValue;

            case LogicalOperatorEnum.LessOrEquals:
                return nValueToCheck <= nMatchValue;

            default:
                throw new Error(
                    `Logical operator ${LogicalOperatorEnum[logicalOperator]} is not correct for checkValueNumberOperations function`,
                );
        }
    }

    static checkValueArrayOperations(
        valueToCheck: unknown,
        matchValue: unknown,
        logicalOperator: LogicalOperatorEnum,
    ): boolean {
        if (valueToCheck == null) {
            return Utils.checkValueStringOperations(valueToCheck, matchValue, logicalOperator);
        }

        if (!isArray(valueToCheck)) {
            return Utils.checkValueStringOperations(valueToCheck, matchValue, logicalOperator);
        }

        let valueArray: any[] = valueToCheck;

        switch (logicalOperator) {
            case LogicalOperatorEnum.StartsWith: {
                if (isEmpty(valueArray)) return false;

                return Utils.checkValueEquals(valueArray[0], matchValue);
            }
            case LogicalOperatorEnum.EndsWith: {
                if (isEmpty(valueArray)) return false;

                return Utils.checkValueEquals(valueArray[valueArray.length - 1], matchValue);
            }
            case LogicalOperatorEnum.Contains: {
                if (isEmpty(valueArray)) return false;

                return valueArray.some((v) => Utils.checkValueEquals(v, matchValue) === true);
            }
            case LogicalOperatorEnum.ContainedIn: {
                if (isEmpty(valueArray)) return true;

                const valueArrayConvertedToString = join(valueArray, '');
                return Utils.checkValueStringOperations(valueArrayConvertedToString, matchValue, logicalOperator);
            }
            case LogicalOperatorEnum.LenEquals: {
                return Utils.checkValueEquals(valueArray.length, matchValue);
            }
            case LogicalOperatorEnum.LenNotEquals: {
                return !Utils.checkValueEquals(valueArray.length, matchValue);
            }
            case LogicalOperatorEnum.LenGreaterThan: {
                return Utils.checkValueNumberOperations(valueArray.length, matchValue, LogicalOperatorEnum.GreaterThan);
            }
            case LogicalOperatorEnum.LenGreaterOrEquals: {
                return Utils.checkValueNumberOperations(
                    valueArray.length,
                    matchValue,
                    LogicalOperatorEnum.GreaterOrEquals,
                );
            }
            case LogicalOperatorEnum.LenLessThan: {
                return Utils.checkValueNumberOperations(valueArray.length, matchValue, LogicalOperatorEnum.LessThan);
            }
            case LogicalOperatorEnum.LenLessOrEquals: {
                return Utils.checkValueNumberOperations(
                    valueArray.length,
                    matchValue,
                    LogicalOperatorEnum.LessOrEquals,
                );
            }

            default:
                throw new Error(
                    `Logical operator ${LogicalOperatorEnum[logicalOperator]} is not correct for checkValueArrayOperations function`,
                );
        }
    }

    static checkValueStringOperations(
        valueToCheck: unknown,
        matchValue: unknown,
        logicalOperator: LogicalOperatorEnum,
    ): boolean {
        let { valueToCheckCaseInsensitive, matchValueCaseInsensitive } = Utils.extractCaseInsentitive(
            valueToCheck,
            matchValue,
        );

        if (!isString(valueToCheckCaseInsensitive)) {
            return false;
        }

        let lenValueToCheck = valueToCheckCaseInsensitive.length;

        //provo convertire matchValue in Number pergli operatori di tipo len
        let lenMatchValue: number = toNumber(matchValue);
        let matchValueIsNumber: boolean = !!matchValue && !isNaN(lenMatchValue);

        switch (logicalOperator) {
            case LogicalOperatorEnum.StartsWith:
                return valueToCheckCaseInsensitive.startsWith(matchValueCaseInsensitive);
            case LogicalOperatorEnum.EndsWith:
                return valueToCheckCaseInsensitive.endsWith(matchValueCaseInsensitive);
            case LogicalOperatorEnum.Contains:
                return valueToCheckCaseInsensitive.includes(matchValueCaseInsensitive);
            case LogicalOperatorEnum.ContainedIn:
                return matchValueCaseInsensitive.includes(valueToCheckCaseInsensitive);
            case LogicalOperatorEnum.LenEquals: {
                if (!matchValueIsNumber) return false;

                return lenValueToCheck == lenMatchValue;
            }
            case LogicalOperatorEnum.LenNotEquals: {
                if (!matchValueIsNumber) return false;

                return lenValueToCheck != lenMatchValue;
            }
            case LogicalOperatorEnum.LenGreaterThan: {
                if (!matchValueIsNumber) return false;

                return lenValueToCheck > lenMatchValue;
            }
            case LogicalOperatorEnum.LenGreaterOrEquals: {
                if (!matchValueIsNumber) return false;

                return lenValueToCheck >= lenMatchValue;
            }
            case LogicalOperatorEnum.LenLessThan: {
                if (!matchValueIsNumber) return false;

                return lenValueToCheck < lenMatchValue;
            }
            case LogicalOperatorEnum.LenLessOrEquals: {
                if (!matchValueIsNumber) return false;

                return lenValueToCheck <= lenMatchValue;
            }

            default:
                throw new Error(
                    `Logical operator ${LogicalOperatorEnum[logicalOperator]} is not correct for checkValueStringOperations function`,
                );
        }
    }

    static getValueArrayOperations(
        valueToCheck: unknown,
        matchValue: unknown,
        logicalOperator: LogicalOperatorEnum,
        itemListPropertyName?: string | null,
    ): unknown {
        if (isNil(valueToCheck)) {
            return null;
        }

        if (!itemListPropertyName) {
            throw new Error(
                `Logical operator ${LogicalOperatorEnum[logicalOperator]} requires an item list property name.`,
            );
        }

        if (!isArray(valueToCheck)) {
            throw new Error(
                `Logical operator ${LogicalOperatorEnum[logicalOperator]} is not correct for not array values: ${valueToCheck}`,
            );
        }

        let valueArray: any[] = valueToCheck;

        switch (logicalOperator) {
            case LogicalOperatorEnum.GetFirstValueNotNull: {
                return find(valueArray, (i) => Utils.checkValueNotNull(i[itemListPropertyName]));
            }
            case LogicalOperatorEnum.GetLastValueNotNull: {
                return findLast(valueArray, (i) => Utils.checkValueNotNull(i[itemListPropertyName]));
            }
            case LogicalOperatorEnum.GetFirstValueEquals: {
                return find(valueArray, (i) => Utils.checkValueEquals(i[itemListPropertyName], matchValue));
            }
            case LogicalOperatorEnum.GetLastValueEquals: {
                return findLast(valueArray, (i) => Utils.checkValueEquals(i[itemListPropertyName], matchValue));
            }
            case LogicalOperatorEnum.GetFirstValueNotEquals: {
                return find(valueArray, (i) => !Utils.checkValueEquals(i[itemListPropertyName], matchValue));
            }
            case LogicalOperatorEnum.GetLastValueNotEquals: {
                return findLast(valueArray, (i) => !Utils.checkValueEquals(i[itemListPropertyName], matchValue));
            }
            case LogicalOperatorEnum.GetFirstValueContains: {
                return find(valueArray, (i) =>
                    Utils.checkValueArrayOperations(i[itemListPropertyName], matchValue, LogicalOperatorEnum.Contains),
                );
            }
            case LogicalOperatorEnum.GetLastValueContains: {
                return findLast(valueArray, (i) =>
                    Utils.checkValueArrayOperations(i[itemListPropertyName], matchValue, LogicalOperatorEnum.Contains),
                );
            }
            case LogicalOperatorEnum.GetFirstValueContainedIn: {
                return find(valueArray, (i) =>
                    Utils.checkValueArrayOperations(
                        i[itemListPropertyName],
                        matchValue,
                        LogicalOperatorEnum.ContainedIn,
                    ),
                );
            }
            case LogicalOperatorEnum.GetLastValueContainedIn: {
                return findLast(valueArray, (i) =>
                    Utils.checkValueArrayOperations(
                        i[itemListPropertyName],
                        matchValue,
                        LogicalOperatorEnum.ContainedIn,
                    ),
                );
            }

            default:
                throw new Error(
                    `Logical operator ${LogicalOperatorEnum[logicalOperator]} is not correct for getValueArrayOperations function`,
                );
        }
    }

    // #region private

    private static extractCaseInsentitive(
        valueToCheck: unknown,
        matchValue: unknown,
    ): {
        valueToCheckCaseInsensitive: unknown;
        matchValueCaseInsensitive: string;
    } {
        const isValueToCheckNull: boolean = !Utils.checkValueNotNull(valueToCheck);
        const isMatchValueNull: boolean = !Utils.checkValueNotNull(matchValue);

        let valueToCheckCaseInsensitive: unknown;
        if (isValueToCheckNull) {
            valueToCheckCaseInsensitive = '';
        } else if (!isString(valueToCheck)) {
            valueToCheckCaseInsensitive = valueToCheck;
        } else {
            valueToCheckCaseInsensitive = valueToCheck.toLowerCase().trim();
        }

        let matchValueCaseInsensitive: string;
        if (isMatchValueNull) {
            matchValueCaseInsensitive = '';
        } else if (!isString(matchValue)) {
            matchValueCaseInsensitive = toString(matchValue).toLowerCase().trim();
        } else {
            matchValueCaseInsensitive = matchValue.toLowerCase().trim();
        }

        return { valueToCheckCaseInsensitive, matchValueCaseInsensitive };
    }

    // #endregion
}
